import { useQuery } from '@tanstack/react-query';
import { frappePost } from '@/lib/frappe';

export const blogKeys = {
  all: ['blog'] as const,
  list: (filters?: Record<string, unknown>) => [...blogKeys.all, 'list', filters] as const,
  article: (slug: string) => [...blogKeys.all, 'article', slug] as const,
  categories: () => [...blogKeys.all, 'categories'] as const,
};

interface BlogArticle {
  name: string;
  title: string;
  title_ar?: string;
  slug: string;
  content: string;
  content_ar?: string;
  excerpt?: string;
  excerpt_ar?: string;
  category?: string;
  cover_image?: string;
  author_name?: string;
  author_name_ar?: string;
  author_image?: string;
  published_date?: string;
  views?: number;
  is_featured?: number;
  tags?: string;
}

interface BlogListResponse {
  articles: BlogArticle[];
  total: number;
  page: number;
  limit: number;
}

export function useBlogArticles(filters?: {
  category?: string;
  search?: string;
  page?: number;
  limit?: number;
  featured?: boolean;
}) {
  return useQuery({
    queryKey: blogKeys.list(filters),
    queryFn: () =>
      frappePost<BlogListResponse>(
        'hudan_lms.api.public.get_blog_articles',
        {
          category: filters?.category,
          search: filters?.search,
          page: filters?.page || 1,
          limit: filters?.limit || 9,
          featured: filters?.featured ? 1 : 0,
        },
        false
      ),
    staleTime: 2 * 60 * 1000,
  });
}

export function useBlogArticle(slug: string) {
  return useQuery({
    queryKey: blogKeys.article(slug),
    queryFn: () =>
      frappePost<BlogArticle>(
        'hudan_lms.api.public.get_blog_article',
        { slug },
        false
      ),
    enabled: !!slug,
  });
}

export function useBlogCategories() {
  return useQuery({
    queryKey: blogKeys.categories(),
    queryFn: async () => {
      const res = await frappePost<{ data?: { categories?: string[] } }>(
        'hudan_lms.api.public.get_blog_categories',
        {},
        false,
      );
      return res?.data?.categories ?? [];
    },
    staleTime: 10 * 60 * 1000,
  });
}
